import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../../components/Header/Header";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomers } from "../../../store/Slices/CustomerSlice";
import { fetchCustomerReturnInvoice } from "../../../store/Slices/CustomerReturnInvoiceSlice";
import AuthInput from "../../../components/Input/AuthInput";
import {
  ErrorToast,
  SuccessToast,
  WarningToast,
} from "../../../utils/ShowToast";
import CustomerSelector from "../../../components/Selector/CustomerSelector";
import CreateInvoiceItemsTable, {
  getCommittedEditInvoiceItems,
  getCommittedInvoiceItems,
  mapInvoiceProductsToItemRows,
} from "../../../components/InoviceAddItem/CreateInvoiceItemsTable";
import {
  GetCustomerReturnSingleApi,
  UpdateCustomerReturnDetailsApi,
} from "../../../ApiRequests";
import SaleType from "../../../components/Input/SaleType";
import InvoiceCustomerType from "../../../components/Input/InvoiceCustomerType";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import ThermalPrintModeSelect from "../../../components/Thermal/ThermalPrintModeSelect";
import ThermalCustomerInvoice from "../../../components/Reports/ThermalCustomerInvoice";
import {
  FiFileText,
  FiPackage,
  FiUser,
  FiXCircle,
  FiSend,
  FiAlertCircle,
} from "react-icons/fi";
import moment from "moment";
import { getAppBasePath } from "../../../utils/appPaths";
import { syncEditDocumentItems } from "../../../utils/invoice";
import { fetchProducts } from "../../../store/Slices/ProductSlice";
import { toLocalYmd } from "../../../utils/dateTime";

const userData = JSON.parse(localStorage.getItem("user")) || {};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-PK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const defaultThermalPrintMode = (guest) =>
  guest ? "hideItemPrices" : "qtyOnly";

const SectionCard = React.forwardRef(
  ({ title, subtitle, icon: Icon, children, className = "" }, ref) => (
    <section
      ref={ref}
      className={`rounded-2xl border border-line bg-card shadow-soft overflow-hidden ${className}`}
    >
      <div className="flex items-start gap-3 px-4 py-3 border-b border-line bg-gradient-to-r from-main/8 to-transparent">
        {Icon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-main text-white">
            <Icon size={18} />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-base font-bold text-ink">{title}</h2>
          {subtitle && (
            <p className="text-sm text-muted mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      <div className="p-4">{children}</div>
    </section>
  )
);
SectionCard.displayName = "SectionCard";

const SummaryRow = ({ label, value, highlight, bold }) => (
  <div
    className={`flex justify-between items-center py-2 text-sm ${
      bold ? "border-t-2 border-main pt-3 mt-1" : "border-b border-line"
    }`}
  >
    <span className={bold ? "font-bold text-ink" : "text-inkSecondary"}>
      {label}
    </span>
    <span
      className={`tabular-nums font-semibold ${
        highlight ? "text-success" : bold ? "text-main text-base" : "text-ink"
      }`}
    >
      {value}
    </span>
  </div>
);

const EditReturn = () => {
  const { returnId: routeReturnId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ItemsState = useSelector((state) => state.ItemsState);

  const saleType = "RETURN";

  const [SelectedCustomer, setSelectedCustomer] = useState("");
  const [OriginalCustomerId, setOriginalCustomerId] = useState("");
  const [invoiceCustomerType, setInvoiceCustomerType] = useState("CUSTOMER");
  const [guestName, setGuestName] = useState("");
  const [guestContact, setGuestContact] = useState("");
  const [guestAddress, setGuestAddress] = useState("");
  const [Payment, setPayment] = useState("");
  const [itemRows, setItemRows] = useState([]);
  const [Discount, setDiscount] = useState(0);
  const [CurDate, setCurDate] = useState("");
  const [InvoiceNo, setInvoiceNo] = useState("");
  const [Description, setDescription] = useState("");
  const [Loading, setLoading] = useState(false);
  const [pageReady, setPageReady] = useState(false);
  const [returnDoc, setReturnDoc] = useState(null);
  const [autoInvoiceNo, setAutoInvoiceNo] = useState(null);

  const [thermalReceiptData, setThermalReceiptData] = useState(null);
  const [thermalPrintMode, setThermalPrintMode] = useState("qtyOnly");
  const [itemsSourceKey, setItemsSourceKey] = useState(0);

  const invoiceNoWrapRef = useRef(null);
  const thermalPrintRef = useRef(null);
  const silentPrintPendingRef = useRef(false);
  const hydratedItemsKeyRef = useRef("");

  const isGuest =
    invoiceCustomerType === "GUEST" || invoiceCustomerType === "WALK_IN";

  const committedItems = useMemo(
    () => getCommittedInvoiceItems(itemRows),
    [itemRows]
  );

  const billTotal = useMemo(
    () => committedItems.reduce((t, i) => t + Number(i.amount || 0), 0),
    [committedItems]
  );

  const totalQty = useMemo(
    () => committedItems.reduce((t, i) => t + Number(i.qty || 0), 0),
    [committedItems]
  );

  const shoppingTotal = useMemo(
    () =>
      committedItems
        .filter((dt) => dt.unit === "SH")
        .reduce((t, i) => t + Number(i.amount || 0), 0),
    [committedItems]
  );

  const netBill = useMemo(
    () => Math.max(0, billTotal - Number(Discount || 0)),
    [billTotal, Discount]
  );

  const customerChanged = useMemo(() => {
    if (isGuest || !OriginalCustomerId || !SelectedCustomer?._id) return false;
    return String(OriginalCustomerId) !== String(SelectedCustomer._id);
  }, [OriginalCustomerId, SelectedCustomer, isGuest]);

  const receiptCustomer = useMemo(() => {
    if (isGuest) {
      return {
        name: guestName.trim() || "Guest",
        contact: guestContact,
        address: guestAddress,
      };
    }
    if (!SelectedCustomer) return null;
    return {
      ...SelectedCustomer,
      contact: guestContact || SelectedCustomer.contact,
      address: guestAddress || SelectedCustomer.address,
    };
  }, [SelectedCustomer, guestContact, guestAddress, guestName, isGuest]);

  const applyReturnPayload = useCallback((data) => {
    if (!data) return;
    setReturnDoc(data);
    setInvoiceNo(data.invoice_no || "");
    setDescription(data.desc || "");
    setCurDate(
      data.date
        ? moment.unix(data.date).format("YYYY-MM-DD")
        : toLocalYmd()
    );
    setDiscount(data.discount ?? 0);
    setPayment(data.payment ?? "");
    setAutoInvoiceNo(data.auto_invoice_no ?? null);
    setOriginalCustomerId(data.customerId?._id || data.customerId || "");
    const type = data.invoice_customer_type || "CUSTOMER";
    setInvoiceCustomerType(type);
    setGuestName(data.guest_name || "");
    setGuestContact(data.guest_contact || "");
    setGuestAddress(data.guest_address || "");
    setThermalPrintMode(
      defaultThermalPrintMode(type === "GUEST" || type === "WALK_IN")
    );
    if (data.customerId && typeof data.customerId === "object") {
      setSelectedCustomer(data.customerId);
    } else {
      setSelectedCustomer("");
    }
    setItemsSourceKey((key) => key + 1);
  }, []);

  const refreshReturn = useCallback(async () => {
    if (!routeReturnId) return;
    const response = await GetCustomerReturnSingleApi(routeReturnId);
    const data = response?.data?.data?.payload;
    if (!data) throw new Error("not found");
    applyReturnPayload(data);
    return data;
  }, [routeReturnId, applyReturnPayload]);

  const handleSilentThermalPrint = useReactToPrint({
    content: () => thermalPrintRef.current,
    documentTitle: `Return-${thermalReceiptData?.invoice_no || "receipt"}`,
    removeAfterPrint: true,
    onAfterPrint: () => {
      silentPrintPendingRef.current = false;
      setThermalReceiptData(null);
      navigate(`${getAppBasePath()}/invoices`);
    },
  });

  const queueSilentPrint = (receipt) => {
    silentPrintPendingRef.current = true;
    setThermalReceiptData(receipt);
  };

  const buildReceiptForPrint = useCallback(
    (savedReturn) => {
      const stock = Array.isArray(ItemsState.data) ? ItemsState.data : [];
      const items =
        savedReturn?.items?.length > 0
          ? getCommittedInvoiceItems(
              mapInvoiceProductsToItemRows(savedReturn.items, stock)
            )
          : committedItems;
      const lineTotal = items.reduce(
        (t, i) => t + Number(i.amount || 0),
        0
      );
      const discountVal = Number(savedReturn?.discount ?? Discount) || 0;
      const paymentVal = Number(savedReturn?.payment ?? Payment) || 0;

      return {
        customer: receiptCustomer,
        invoice_no:
          (savedReturn?.invoice_no && String(savedReturn.invoice_no).trim()) ||
          InvoiceNo.trim() ||
          "",
        auto_invoice_no:
          savedReturn?.auto_invoice_no ?? autoInvoiceNo ?? "",
        date: savedReturn?.date
          ? moment.unix(savedReturn.date).format("YYYY-MM-DD")
          : CurDate,
        desc: savedReturn?.desc ?? Description,
        items,
        discount: discountVal,
        payment: paymentVal,
        billTotal: lineTotal,
        saleType,
        invoiceCustomerType,
        printMode: thermalPrintMode,
      };
    },
    [
      ItemsState.data,
      committedItems,
      receiptCustomer,
      InvoiceNo,
      autoInvoiceNo,
      CurDate,
      Description,
      Discount,
      Payment,
      invoiceCustomerType,
      thermalPrintMode,
    ]
  );

  useEffect(() => {
    if (!silentPrintPendingRef.current || !thermalReceiptData) return;
    const timer = setTimeout(() => {
      silentPrintPendingRef.current = false;
      handleSilentThermalPrint();
    }, 350);
    return () => clearTimeout(timer);
  }, [thermalReceiptData, handleSilentThermalPrint]);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (!routeReturnId) {
      navigate(`${getAppBasePath()}/invoices`, { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await refreshReturn();
        if (!cancelled) setPageReady(true);
      } catch {
        if (!cancelled) {
          ErrorToast("Failed to load return");
          navigate(`${getAppBasePath()}/invoices`, { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeReturnId, navigate, refreshReturn]);

  useEffect(() => {
    if (!returnDoc || !itemsSourceKey) return;
    const stock = Array.isArray(ItemsState.data) ? ItemsState.data : [];
    const hasStock = stock.length > 0;
    const hydrateKey = `${returnDoc._id}:${itemsSourceKey}:${
      hasStock ? "stock" : "nostock"
    }`;
    if (hydratedItemsKeyRef.current === hydrateKey) return;
    if (
      hydratedItemsKeyRef.current.startsWith(
        `${returnDoc._id}:${itemsSourceKey}:`
      ) &&
      hydratedItemsKeyRef.current.endsWith(":stock")
    ) {
      return;
    }
    hydratedItemsKeyRef.current = hydrateKey;
    setItemRows(mapInvoiceProductsToItemRows(returnDoc.items || [], stock));
  }, [returnDoc, itemsSourceKey, ItemsState.data]);

  useEffect(() => {
    if (SelectedCustomer && !isGuest) {
      setGuestContact(SelectedCustomer.contact || "");
      setGuestAddress(SelectedCustomer.address || "");
    }
  }, [SelectedCustomer, isGuest]);

  useEffect(() => {
    if (isGuest && pageReady) {
      setPayment(String(netBill));
    }
  }, [isGuest, netBill, pageReady]);

  const onSubmit = async (e, type = 1) => {
    e.preventDefault();
    setLoading(true);

    if (!isGuest && !SelectedCustomer) {
      WarningToast("Select Customer!");
    } else if (!isGuest && !Description.trim()) {
      WarningToast("Enter Description!");
    } else if (!CurDate) {
      WarningToast("Enter a valid Date");
    } else if (!committedItems.length) {
      WarningToast("Add at least one item!");
    } else if (isGuest && Number(Payment) < netBill) {
      WarningToast("Guest return requires full cash refund!");
    } else {
      try {
        const editRows = getCommittedEditInvoiceItems(itemRows);

        await syncEditDocumentItems({
          documentId: routeReturnId,
          isReturn: true,
          accountCustomerId: isGuest ? undefined : OriginalCustomerId,
          originalItems: returnDoc?.items || [],
          committedRows: editRows,
        });

        const response = await UpdateCustomerReturnDetailsApi(routeReturnId, {
          ...(isGuest
            ? {
                guest_name: guestName.trim() || "Guest",
                guest_contact: guestContact,
                guest_address: guestAddress,
                payment: netBill,
              }
            : { customerId: SelectedCustomer._id }),
          invoice_no: InvoiceNo.trim(),
          desc: Description,
          date: CurDate,
          discount: Number(Discount) || 0,
          branchId:
            returnDoc?.branchId?._id ||
            returnDoc?.branchId ||
            userData.branchId,
        });

        if (response.data.success) {
          SuccessToast("Return updated successfully");
          dispatch(fetchCustomers());
          dispatch(fetchCustomerReturnInvoice());
          dispatch(fetchProducts());

          const saved = response.data?.data?.payload;

          if (type === 2) {
            queueSilentPrint(buildReceiptForPrint(saved));
          } else {
            await refreshReturn();
            navigate(`${getAppBasePath()}/invoices`);
          }
        } else {
          ErrorToast(response.data.error?.msg || "Failed to update");
        }
      } catch (err) {
        ErrorToast(err.response?.data?.error?.msg || "Something went wrong");
      }
    }
    setLoading(false);
  };

  const handleClear = () => {
    if (window.confirm("Reload return from server? Unsaved header changes will be lost.")) {
      refreshReturn();
    }
  };

  if (!pageReady) {
    return (
      <div className="w-full flex flex-col px-4 pb-10 font-quicksand">
        <Header title="Edit Return" desc="Loading…" Back />
        <div className="mt-8">
          <FetchingLoading />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col px-4 pb-10 font-quicksand">
      <Header
        title="Edit Return"
        desc={isGuest ? "Guest return invoice" : "Customer return invoice"}
        Back
      />

      <SectionCard
        title="Party & transaction"
        subtitle="Return type, customer or guest, and contact details"
        icon={FiUser}
        className="mb-4"
      >
        <div
          className="flex flex-wrap gap-6 items-end mb-5 opacity-80 pointer-events-none select-none"
          aria-hidden
        >
          <SaleType saleType={saleType} setSaleType={() => {}} />
          <InvoiceCustomerType
            customerType={invoiceCustomerType}
            setCustomerType={() => {}}
          />
        </div>

        {isGuest ? (
          <div className="space-y-4 pt-4 border-t border-line">
            <div className="grid grid-cols-3 max680:grid-cols-1 gap-4">
              <AuthInput
                label="Guest Name"
                placeholder="Guest (optional)"
                Type="text"
                Value={guestName}
                setValue={setGuestName}
                Width="w-full"
              />
              <AuthInput
                label="Contact"
                placeholder="Enter Contact #"
                Type="text"
                Value={guestContact}
                setValue={setGuestContact}
                Width="w-full"
              />
              <AuthInput
                label="Address"
                placeholder="Enter Address"
                Type="text"
                Value={guestAddress}
                setValue={setGuestAddress}
                Width="w-full"
              />
            </div>
            <p className="text-sm text-warning bg-warningSoft border border-warning/20 rounded-lg px-4 py-3">
              Guest return — full cash refund required.
            </p>
          </div>
        ) : (
          <div className="pt-4 border-t border-line">
            <CustomerSelector
              SelectedCustomer={SelectedCustomer}
              setSelectedCustomer={setSelectedCustomer}
            />
          </div>
        )}

        <div
          ref={invoiceNoWrapRef}
          className="grid grid-cols-2 max680:grid-cols-1 gap-4 pt-4 mt-4 border-t border-line scroll-mt-4"
        >
          <AuthInput
            label="Invoice No (optional)"
            Named="SaleBill"
            placeholder="Optional — auto # assigned"
            Type="number"
            Value={InvoiceNo}
            setValue={setInvoiceNo}
            Width="w-full"
          />
          <div className="relative mb-[15px] w-full font-[Quicksand]">
            <p className="absolute top-[-11px] left-3 w-fit bg-white h-[13px] text-[15px] font-bold text-main">
              {isGuest ? "Description (optional)" : "Description"}
            </p>
            <textarea
              value={Description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                isGuest ? "Optional description" : "Enter Description"
              }
              rows={3}
              className="px-3 py-2 border border-line rounded-lg w-full outline-none focus:border-main focus:ring-2 focus:ring-main/15 resize-y min-h-[46px] h-[46px] text-sm text-ink placeholder:text-muted"
            />
          </div>
        </div>
        {autoInvoiceNo != null && (
          <p className="text-xs text-muted mt-1">
            System invoice #{" "}
            <span className="font-semibold text-main">{autoInvoiceNo}</span>
          </p>
        )}
      </SectionCard>

      {customerChanged && (
        <div className="flex gap-3 rounded-xl border border-warning/30 bg-warningSoft px-4 py-3 text-warning mb-4">
          <FiAlertCircle className="shrink-0 mt-0.5" size={20} />
          <div className="text-sm">
            <p className="font-bold">Customer changed</p>
            <p className="mt-0.5">
              Submit will move this return to{" "}
              <strong>{SelectedCustomer.name}</strong>.
            </p>
          </div>
        </div>
      )}

      <SectionCard
        title="Return items"
        subtitle={
          SelectedCustomer && !isGuest
            ? `Editing return items for ${SelectedCustomer.name}`
            : isGuest
            ? "Editing guest return items"
            : "Add products to this return"
        }
        icon={FiPackage}
        className="mb-4 scroll-mt-4"
      >
        <CreateInvoiceItemsTable
          items={itemRows}
          setItems={setItemRows}
          saleType={saleType}
          customerAccountType={isGuest ? undefined : SelectedCustomer?.type}
        />
      </SectionCard>

      <div className="flex flex-row flex-wrap gap-4 mt-4">
        <div className="flex-[2] min-w-[min(100%,520px)]">
          <SectionCard
            title="Date & discount"
            subtitle={isGuest ? "Date, discount, and refund" : "Date and discount"}
            icon={FiFileText}
          >
            <div className="grid grid-cols-2 max680:grid-cols-1 gap-x-4 gap-y-1">
              <AuthInput
                label="Date"
                placeholder="Select Date"
                Type="date"
                Value={CurDate}
                setValue={setCurDate}
                Width="w-full"
              />
              <AuthInput
                label="Discount"
                placeholder="0"
                Type="number"
                Value={Discount}
                setValue={setDiscount}
                Width="w-full"
              />
              {isGuest && (
                <AuthInput
                  label="Cash Refund (Full)"
                  placeholder="0"
                  Type="number"
                  Value={Payment}
                  setValue={setPayment}
                  disabled
                  Width="w-full"
                />
              )}
            </div>
            {SelectedCustomer && !isGuest && (
              <div className="grid grid-cols-2 max680:grid-cols-1 gap-x-4 gap-y-1 mt-3 pt-3 border-t border-line">
                <AuthInput
                  label="Contact"
                  placeholder="Enter Contact #"
                  Type="text"
                  Value={guestContact}
                  setValue={setGuestContact}
                  Width="w-full"
                />
                <AuthInput
                  label="Address"
                  placeholder="Enter Address"
                  Type="text"
                  Value={guestAddress}
                  setValue={setGuestAddress}
                  Width="w-full"
                />
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-line">
              <ThermalPrintModeSelect
                compact
                value={thermalPrintMode}
                onChange={setThermalPrintMode}
              />
            </div>
          </SectionCard>
        </div>

        <div className="flex-1 min-w-[280px]">
          <div className="rounded-xl border border-line bg-card shadow-card overflow-hidden sticky top-4">
            <div className="bg-subtle px-4 py-3 border-b border-line">
              <p className="text-xs font-bold uppercase tracking-widest text-muted">
                Summary
              </p>
            </div>
            <div className="p-4">
              <SummaryRow label="Total Qty" value={fmt(totalQty)} />
              <SummaryRow label="Return Amount" value={fmt(billTotal)} />
              <SummaryRow label="Shopping Total" value={fmt(shoppingTotal)} />
              <SummaryRow label="Discount" value={fmt(Discount)} />
              {isGuest && (
                <SummaryRow label="Refund" value={fmt(Payment)} />
              )}
              <SummaryRow label="Net Return" value={fmt(netBill)} bold />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-3 mt-6 pt-2">
        <button
          type="button"
          onClick={handleClear}
          disabled={Loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-danger/30 bg-white text-danger text-sm font-bold hover:bg-dangerSoft disabled:opacity-50 transition-colors active:scale-[.98]"
        >
          <FiXCircle size={18} />
          Clear
        </button>
        <button
          type="button"
          onClick={(e) => onSubmit(e, 2)}
          disabled={Loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-main bg-white text-main text-sm font-bold hover:bg-main/5 disabled:opacity-50 transition-colors"
        >
          <FiSend size={18} />
          Submit &amp; print
        </button>
        <button
          type="button"
          onClick={(e) => onSubmit(e, 1)}
          disabled={Loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-main text-white text-sm font-bold hover:bg-main/90 shadow-sm disabled:opacity-50 transition-colors"
        >
          <FiSend size={18} />
          Submit
        </button>
      </div>

      {Loading && (
        <div className="mt-4">
          <FetchingLoading />
        </div>
      )}

      {thermalReceiptData && (
        <div
          aria-hidden
          className="fixed pointer-events-none opacity-0"
          style={{ left: -10000, top: 0 }}
        >
          <ThermalCustomerInvoice
            ref={thermalPrintRef}
            data={{ ...thermalReceiptData, printMode: thermalPrintMode }}
          />
        </div>
      )}
    </div>
  );
};

export default EditReturn;
